import React, { useEffect, useState } from 'react';
import { ArrowRight, GitBranch, X } from 'lucide-react';
import LlmOrchestrationFlow from './LlmOrchestrationFlow';
import type { LlmInspectorCall } from './llmInspectorData';
import { CALL_TYPE_LABELS } from './llmInspectorData';
import { useObsTheme } from './theme';

interface Props {
  call: LlmInspectorCall | null;
  onClose: () => void;
}

export default function LlmFlowSimulationModal({ call, onClose }: Props) {
  const t = useObsTheme();
  const [visible, setVisible] = useState(false);
  const open = call !== null;

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open || !call) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity duration-200 ${
        t.isLight ? 'bg-black/40' : 'bg-black/70'
      } ${visible ? 'opacity-100' : 'opacity-0'}`}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`w-full max-w-6xl rounded-xl border shadow-2xl flex flex-col max-h-[92vh] transition-all duration-200 ${
          t.isLight ? 'bg-white border-slate-200' : 'bg-neutral-900 border-white/10'
        } ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="llm-flow-modal-title"
      >
        <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b shrink-0 ${t.cardHeaderBorder}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
                t.isLight ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-indigo-500/15 border-indigo-500/30 text-indigo-400'
              }`}
            >
              <GitBranch className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 id="llm-flow-modal-title" className={`text-sm font-semibold ${t.title}`}>
                {call.label} — Flow Simulation
              </h2>
              <p className={`text-[11px] ${t.subtitle} mt-0.5 flex items-center gap-1 flex-wrap`}>
                <span className={`px-1.5 py-0 rounded text-[10px] font-medium ${t.isLight ? 'bg-slate-100' : 'bg-white/10'}`}>
                  {CALL_TYPE_LABELS[call.type]}
                </span>
                <span>{call.from}</span>
                <ArrowRight className="w-3 h-3 shrink-0" />
                <span>{call.to}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg shrink-0 ${t.iconMuted} ${t.iconHover}`}
            aria-label="Close simulation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 min-h-0">
          <LlmOrchestrationFlow key={call.id} call={call} />
        </div>
      </div>
    </div>
  );
}
